import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { LayoutGrid, List, Plus, Search } from 'lucide-react';
import { Input } from '@/components/ui-kit/input';
import { Button } from '@/components/ui-kit/button';
import { Skeleton } from '@/components/ui-kit/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui-kit/select';
import { useToast } from '@/hooks/use-toast';
import {
  useGetHotWheels,
  useUpdateHotWheel,
} from '../../hooks/use-hotwheels';
import { HotWheel, SortField, SortDir } from '../../types/hotwheels.types';
import { HotWheelCard } from '../../component/hotwheel-card/hotwheel-card';
import { EditHotwheelModal } from '../../component/edit-hotwheel-modal/edit-hotwheel-modal';
import { DeleteHotwheelDialog } from '../../component/delete-hotwheel-dialog/delete-hotwheel-dialog';
import { EmptyState } from '../../component/empty-state/empty-state';

/**
 * Hot Wheels collection page — grid view of the user's cars.
 *
 * Header:
 *   - "My Collection" title + total count
 *   - Search box (live, client-side filter on Brand / Series / ModelNumber)
 *   - View toggle (grid / list)
 *   - "Add Car" CTA
 *
 * Body:
 *   - Loading: 8-card skeleton
 *   - Empty: hero CTA
 *   - Populated: cards + modals for edit / delete
 */
export function HotWheelsCollection() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [search, setSearch] = useState('');
  const [view, setView] = useState<'grid' | 'list'>('grid');
  const [sortField, setSortField] = useState<SortField>('CreatedDate');
  const [sortDir, setSortDir] = useState<SortDir>('DESC');
  const [brandFilter, setBrandFilter] = useState<string>('all');
  const [conditionFilter, setConditionFilter] = useState<string>('all');

  const [editingCar, setEditingCar] = useState<HotWheel | null>(null);
  const [deletingCar, setDeletingCar] = useState<HotWheel | null>(null);

  const { mutateAsync: updateCar } = useUpdateHotWheel();
  const { data, isLoading, isError, error } = useGetHotWheels({
    pageNo: 0,
    pageSize: 100,
    order: [{ field: sortField, direction: sortDir }],
  });

  const items = data?.items ?? [];

  // Client-side filter + search.
  const filtered = useMemo(() => {
    let result = items;
    if (search) {
      const q = search.toLowerCase();
      result = result.filter(
        (c) =>
          c.Brand?.toLowerCase().includes(q) ||
          c.Series?.toLowerCase().includes(q) ||
          c.ModelNumber?.toLowerCase().includes(q)
      );
    }
    if (brandFilter !== 'all') {
      result = result.filter((c) => c.Brand === brandFilter);
    }
    if (conditionFilter !== 'all') {
      result = result.filter((c) => c.Condition === conditionFilter);
    }
    return result;
  }, [items, search, brandFilter, conditionFilter]);

  const uniqueBrands = useMemo(
    () => Array.from(new Set(items.map((c) => c.Brand).filter(Boolean))).sort(),
    [items]
  );
  const uniqueConditions = useMemo(
    () => Array.from(new Set(items.map((c) => c.Condition).filter(Boolean))).sort(),
    [items]
  );

  const handleToggleFavorite = async (car: HotWheel) => {
    try {
      await updateCar({
        itemId: car.ItemId,
        input: { Favorite: !car.Favorite },
      });
    } catch (err) {
      toast({
        title: t('SAVE_FAILED'),
        description: err instanceof Error ? err.message : String(err),
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white">{t('MY_COLLECTION')}</h1>
          <p className="text-zinc-400 text-sm mt-1">
            {items.length} {items.length === 1 ? 'car' : 'cars'}
          </p>
        </div>
        <Link
          to="/collection/new"
          className="rounded-md bg-[#2563EB] hover:bg-blue-600 text-white px-4 py-2 text-sm font-semibold inline-flex items-center gap-2 self-start md:self-auto"
        >
          <Plus className="h-4 w-4" /> {t('ADD_CAR')}
        </Link>
      </div>

      {/* Toolbar */}
      <div className="flex flex-col md:flex-row gap-3 items-stretch md:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
          <Input
            placeholder={t('SEARCH_PLACEHOLDER')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 bg-zinc-900 border-zinc-800 text-white"
          />
        </div>
        <Select value={brandFilter} onValueChange={setBrandFilter}>
          <SelectTrigger className="md:w-44 bg-zinc-900 border-zinc-800 text-white">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('ALL_BRANDS')}</SelectItem>
            {uniqueBrands.map((b) => (
              <SelectItem key={b} value={b}>{b}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={conditionFilter} onValueChange={setConditionFilter}>
          <SelectTrigger className="md:w-44 bg-zinc-900 border-zinc-800 text-white">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('ALL_CONDITIONS')}</SelectItem>
            {uniqueConditions.map((c) => (
              <SelectItem key={c} value={c}>{c}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={sortField} onValueChange={(v) => setSortField(v as SortField)}>
          <SelectTrigger className="md:w-44 bg-zinc-900 border-zinc-800 text-white">
            <SelectValue placeholder={t('SORT_BY')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="CreatedDate">{t('RECENTLY_ADDED')}</SelectItem>
            <SelectItem value="Brand">Brand</SelectItem>
            <SelectItem value="ReleaseYear">{t('RELEASE_YEAR')}</SelectItem>
            <SelectItem value="EstimatedValue">{t('ESTIMATED_VALUE')}</SelectItem>
            <SelectItem value="PurchaseDate">{t('PURCHASE_DATE')}</SelectItem>
          </SelectContent>
        </Select>
        <Button
          variant={sortDir === 'DESC' ? 'default' : 'outline'}
          size="icon"
          onClick={() => setSortDir(sortDir === 'DESC' ? 'ASC' : 'DESC')}
          className="bg-zinc-900 border-zinc-800 text-white"
          title={sortDir}
        >
          {sortDir === 'DESC' ? '↓' : '↑'}
        </Button>
        <div className="flex rounded-md border border-zinc-800 overflow-hidden">
          <button
            onClick={() => setView('grid')}
            className={`px-3 py-2 ${view === 'grid' ? 'bg-zinc-800 text-white' : 'text-zinc-400'}`}
            title={t('VIEW_GRID')}
          >
            <LayoutGrid className="h-4 w-4" />
          </button>
          <button
            onClick={() => setView('list')}
            className={`px-3 py-2 ${view === 'list' ? 'bg-zinc-800 text-white' : 'text-zinc-400'}`}
            title={t('VIEW_LIST')}
          >
            <List className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Body */}
      {isLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} className="aspect-[4/3] rounded-2xl bg-zinc-900" />
          ))}
        </div>
      ) : isError ? (
        <div className="text-red-400">{(error as Error)?.message}</div>
      ) : items.length === 0 ? (
        <EmptyState ctaHref="/collection/new" />
      ) : filtered.length === 0 ? (
        <EmptyState
          title="No matches"
          description="Try a different search or filter."
          ctaLabel="Clear filters"
          onCtaClick={() => {
            setSearch('');
            setBrandFilter('all');
            setConditionFilter('all');
          }}
        />
      ) : (
        <div
          className={
            view === 'grid'
              ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5'
              : 'flex flex-col gap-3'
          }
        >
          {filtered.map((car) => (
            <HotWheelCard
              key={car.ItemId}
              car={car}
              onEdit={setEditingCar}
              onDelete={setDeletingCar}
              onToggleFavorite={handleToggleFavorite}
            />
          ))}
        </div>
      )}

      <EditHotwheelModal
        car={editingCar}
        open={!!editingCar}
        onOpenChange={(o) => !o && setEditingCar(null)}
      />
      <DeleteHotwheelDialog
        car={deletingCar}
        open={!!deletingCar}
        onOpenChange={(o) => !o && setDeletingCar(null)}
      />
    </div>
  );
}